import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { FilterGroupVisibility } from '../../../../models/api/reports/filter-security';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { AccountUnitCriteriaComponent } from './account-unit-criteria.component';
import { CorporateStructureCriteriaComponent } from './corporate-structure-criteria.component';

@Component({
  selector: 'app-location-criteria',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RadioButtonComponent,
    AccountUnitCriteriaComponent,
    CorporateStructureCriteriaComponent,
  ],
  template: `
    <div class="tw-pt-2">
      @if (showCorporateStructure) {
        <div class="tw-flex tw-items-center tw-gap-4 tw-pb-4">
          <span class="tw-font-bold">Filter By:</span>
          <app-radio-button
            [control]="displayFilterType"
            screenReaderLabel="Filter by"
            optionLabel="name"
            optionValue="code"
            [options]="filterTypeOptions"
            [showHelpText]="false"
            [vertical]="false"
          />
        </div>
      }

      @if (fg.controls.filterType.value === 'acctunit') {
        <app-account-unit-criteria
          #accountUnitRef
          [contractNumber]="contractNumber"
          [includeType]="fg.controls.acctIncludeType.value"
          [initialCriteria]="acctUnitInitialCriteria"
          (includeTypeChange)="fg.controls.acctIncludeType.setValue($event)"
          (selectionChange)="emitCount()"
        />
      }
      @if (fg.controls.filterType.value === 'corpstruct') {
        <app-corporate-structure-criteria
          #corpStructRef
          [contractNumber]="contractNumber"
          [includeType]="fg.controls.corpIncludeType.value"
          [initialCriteria]="corpStructInitialCriteria"
          (includeTypeChange)="fg.controls.corpIncludeType.setValue($event)"
          (selectionChange)="emitCount()"
        />
      }
    </div>
  `,
  styles: ``,
})
export class LocationCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  private reports = inject(ReportsService);

  @Input({ required: true })
  section: FilterSection;

  @Input()
  filterGroupVisibility: FilterGroupVisibility | null = null;

  @Input()
  showCorporateStructure = true;

  @ViewChild('accountUnitRef')
  accountUnitCriteria: AccountUnitCriteriaComponent;
  @ViewChild('corpStructRef')
  corpStructCriteria: CorporateStructureCriteriaComponent;

  contractNumber = 0;
  acctUnitInitialCriteria: FilterCriteria[] = [];
  corpStructInitialCriteria: FilterCriteria[] = [];

  fg = new FormGroup({
    filterType: new FormControl<string>('acctunit'),
    acctIncludeType: new FormControl<'include' | 'exclude'>('include'),
    corpIncludeType: new FormControl<'include' | 'exclude'>('include'),
  });

  /**
   * Req 3.8.010: Dynamic filter type options
   * Hides Corporate Structure option if INSCOR restricted
   */
  get filterTypeOptions() {
    const options = [{ name: 'Accounts/Units', code: 'acctunit' }];
    if (!this.filterGroupVisibility?.isCorpStructRestricted) {
      options.push({ name: 'Corporate Structure', code: 'corpstruct' });
    }
    return options;
  }

  displayFilterType = new FormControl<string>('acctunit');
  private isRevertingFilterType = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.section.criteria = this;

    const client = this.reports.selectedClient();
    if (client) {
      this.contractNumber = Number(client);
    }

    if (this.section.values?.length) {
      this.setLocationFromCriteria(this.section.values);
    }

    let initialFilterType = this.fg.controls.filterType.value || 'acctunit';
    if (
      initialFilterType === 'corpstruct' &&
      (!this.showCorporateStructure ||
        this.filterGroupVisibility?.isCorpStructRestricted)
    ) {
      initialFilterType = 'acctunit';
      this.fg.controls.filterType.setValue('acctunit');
    }
    this.displayFilterType.setValue(initialFilterType, { emitEvent: false });

    this.dispose.subs = this.displayFilterType.valueChanges.subscribe(
      (newValue) => {
        this.onDisplayFilterTypeChange(newValue);
      },
    );
  }

  private setLocationFromCriteria(criteria: FilterCriteria[]) {
    const acctCriteria = criteria.filter(
      (c) => c.column === 'ACCOUNT_AND_UNIT' || c.column === 'ACCOUNT_ONLY',
    );
    const corpCriteria = criteria.filter(
      (c) =>
        c.tableAlias === 'filter_corp_struct_levels' &&
        c.column === 'NODE_ID_LIST',
    );

    if (corpCriteria.length) {
      this.fg.controls.filterType.setValue('corpstruct', { emitEvent: false });
      const operator = corpCriteria[0].operator;
      if (operator === 'NOT_LIKE') {
        this.fg.controls.corpIncludeType.setValue('exclude');
      }
      this.corpStructInitialCriteria = corpCriteria;
    } else if (acctCriteria.length) {
      this.fg.controls.filterType.setValue('acctunit', { emitEvent: false });
      const operator = acctCriteria[0].operator;
      if (operator === 'NOTEQUALTO') {
        this.fg.controls.acctIncludeType.setValue('exclude');
      }
      this.acctUnitInitialCriteria = acctCriteria;
    }
  }

  emitCount() {
    const filterType = this.fg.controls.filterType.value;
    let count = 0;

    if (filterType === 'acctunit' && this.accountUnitCriteria) {
      count = this.accountUnitCriteria.getCount();
    } else if (filterType === 'corpstruct' && this.corpStructCriteria) {
      count = this.corpStructCriteria.getCount();
    }

    this.section.count.set(count);
  }

  private onDisplayFilterTypeChange(newValue: string) {
    if (this.isRevertingFilterType) {
      this.isRevertingFilterType = false;
      return;
    }

    const currentType = this.fg.controls.filterType.value;

    if (newValue === currentType) {
      return;
    }

    const hasSelections =
      currentType === 'acctunit'
        ? (this.accountUnitCriteria?.getCount() ?? 0) > 0
        : (this.corpStructCriteria?.getCount() ?? 0) > 0;

    if (hasSelections) {
      this.confirm.show({
        label: 'Change location selection',
        message:
          'Are you sure you want to change the Select by options? All previously selected locations will be removed.',
        width: '35rem',
        confirm: () => {
          this.accountUnitCriteria?.clearSelection();
          this.corpStructCriteria?.clearSelection();
          this.fg.controls.filterType.setValue(newValue);
          this.emitCount();
        },
        cancel: () => {
          this.isRevertingFilterType = true;
          this.displayFilterType.setValue(currentType);
        },
      });
    } else {
      this.fg.controls.filterType.setValue(newValue);
      this.emitCount();
    }
  }

  valid(): boolean {
    return true;
  }

  getCriteria(): FilterCriteria[] {
    const filterType = this.fg.controls.filterType.value;

    if (filterType === 'acctunit' && this.accountUnitCriteria) {
      return this.accountUnitCriteria.getCriteria();
    }
    if (filterType === 'corpstruct' && this.corpStructCriteria) {
      return this.corpStructCriteria.getCriteria();
    }

    return [];
  }

  clearCriteria(): void {
    this.accountUnitCriteria?.clearSelection();
    this.corpStructCriteria?.clearSelection();
    this.fg.controls.filterType.setValue('acctunit');
    this.isRevertingFilterType = true;
    this.displayFilterType.setValue('acctunit');
    this.fg.controls.acctIncludeType.setValue('include');
    this.fg.controls.corpIncludeType.setValue('include');
    this.emitCount();
  }
}
